import * as React from "react";
import { useEffect } from "react";

import { AbsoluteModelPath, createRelativeFromTokens, PathToken } from "../../../../../../Common/ModelPath/ModelPath";
import { ModelHunk } from "../../../../Engine/Core/ModelStoring/ModelHunk";
import {
    DataPasteContext,
    DatePasteStatus,
    RegisterTableDataPasteFunction,
} from "../../MultiLine/TableDataPasteContext";
import { arrayIsNotEmpty, isNotNullOrUndefined, reject } from "../../../../../../Common/TypingUtils";
import { IterUtils } from "../../../../../../Common/IterableUtils";
import { FocusManagementObserver } from "../../../Services/FocusManagement/FocusManagementObserver";
import { emptyRef } from "../../../TypingUtils";

import { BodyRowRoot } from "./StickyTableBodyRow.styles";

export interface StickyTableBodyRowProps {
    style?: React.CSSProperties;
    children?: React.ReactNode;
    showBottomBorder?: boolean;
    columnsStructure?: AbsoluteModelPath[];
    registerRowPasteFunc?: (func: RegisterTableDataPasteFunction) => void;
    fillMultiline: (bindingPath: AbsoluteModelPath, values: ModelHunk, newInstanceCount: number) => void;
    bindingPath?: AbsoluteModelPath;
    processTableDataPaste?: (
        clipboardContent: DataTransfer,
        context: DataPasteContext,
        columnIndex?: number
    ) => DatePasteStatus;
}

export const StickyTableBodyRow = (props: StickyTableBodyRowProps) => {
    const rowRef = React.useRef<HTMLTableRowElement | null>(emptyRef);

    useEffect(() => {
        if (props.columnsStructure?.length != undefined && props.columnsStructure?.length > 0) {
            props.registerRowPasteFunc?.((excelData, _, columnStartIndex) =>
                pasteTableData(excelData, columnStartIndex)
            );
        }
    }, []);

    const handleRequestFocus = () => {
        if (rowRef.current) {
            rowRef.current?.scrollIntoView({ block: "center" });
        }
    };

    const getColumnStartIndex = (currentBindingPath: AbsoluteModelPath) =>
        props.columnsStructure?.findIndex(columnPath => columnPath.isEquals(currentBindingPath));

    const fillInstance = (rowData: string[], columnStartIndex: number, columns: PathToken[]) => {
        const result: ModelHunk = new ModelHunk();
        const rightBorder = Math.min(rowData.length, columns.length - columnStartIndex);

        for (let i = 0; i < rightBorder; i++) {
            if (columns[columnStartIndex + i] != undefined) {
                const path = createRelativeFromTokens([columns[columnStartIndex + i] ?? reject()]);
                result.set(path, {
                    value: rowData[i],
                });
            }
        }

        return result;
    };

    const pasteTableData = (excelData: string[][], columnStartIndex?: number): DatePasteStatus => {
        if (props.columnsStructure == undefined || excelData.length === 0 || columnStartIndex == undefined) {
            return DatePasteStatus.Failure;
        }

        const columnNames = props.columnsStructure
            .filter((elem, pos, arr) => arr.indexOf(elem) == pos)
            .map(bp => IterUtils.last(bp.getPathParts()))
            .filter(isNotNullOrUndefined);
        if (
            arrayIsNotEmpty(excelData) &&
            props.columnsStructure != undefined &&
            arrayIsNotEmpty(props.columnsStructure)
        ) {
            const parentPath = props.columnsStructure[0]?.getParentPath();
            if (parentPath != undefined) {
                props.fillMultiline(parentPath, fillInstance(excelData[0], columnStartIndex, columnNames), 0);
            }
        }

        return DatePasteStatus.SuccessOne;
    };

    return (
        <DataPasteContext.Provider
            value={(dataTransfer, context) =>
                props.processTableDataPaste
                    ? props.processTableDataPaste(dataTransfer, context, getColumnStartIndex(context.bindingPath))
                    : DatePasteStatus.Failure
            }>
            <FocusManagementObserver onRequestFocus={handleRequestFocus}>
                <BodyRowRoot ref={rowRef} showBottomBorder={props.showBottomBorder} style={props.style}>
                    {props.children}
                </BodyRowRoot>
            </FocusManagementObserver>
        </DataPasteContext.Provider>
    );
};
